import { useEffect, useRef, useState } from 'react';

import { useAui } from '@assistant-ui/react';

import { useAssistantCloudStore } from '@/lib/assistant-cloud-local/stores/assistant-cloud-store';
import { useHistorySession } from '@/lib/history-key/session';
import { findThread } from '@/lib/utils/thread';

export function useHistoryLock({
  hasStorageAccess = true,
  storageChecked = true,
  enabled = true,
} = {}): void {
  const api = useAui();
  const owner = useHistorySession(state => state.owner);
  const previous = useRef({ owner, access: storageChecked ? hasStorageAccess : null, lost: false });
  const [attempt, setAttempt] = useState({ owner, access: hasStorageAccess, count: 0 });

  useEffect(() => {
    const changed = owner !== previous.current.owner;
    const storageChanged =
      previous.current.access !== null && hasStorageAccess !== previous.current.access;
    // A failed reopen keeps its sign-out pending for the retry, but a sign-in since then is its own
    // change: it must not leave the conversation whose call it resumes.
    const lostAccount =
      (previous.current.lost && !changed) ||
      (changed && previous.current.owner !== null) ||
      (storageChanged && !hasStorageAccess);
    previous.current = {
      owner,
      access: storageChecked ? hasStorageAccess : null,
      lost: lostAccount,
    };
    if (!storageChecked || !enabled) return;
    const count =
      !changed && !storageChanged && attempt.owner === owner && attempt.access === hasStorageAccess
        ? attempt.count
        : 0;
    const store = useAssistantCloudStore.getState();
    if (
      !count &&
      !lostAccount &&
      !storageChanged &&
      (hasStorageAccess ? owner === store.openedFor : true)
    )
      return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const isCurrent = () => !cancelled && useHistorySession.getState().owner === owner;
    async function reopen() {
      // Let an existing load settle before resetting its state.
      await store.initPromise?.catch(() => undefined);
      if (!isCurrent()) return;
      if (await reopenHistory({ api, lostAccount, isCurrent })) {
        previous.current.lost = false;
        if (count) setAttempt({ owner, access: hasStorageAccess, count: 0 });
      }
    }
    void reopen().catch(error => {
      if (!isCurrent()) return;
      console.error('Failed to reopen conversation history', error);
      if (count < 2)
        timer = setTimeout(
          () => setAttempt({ owner, access: hasStorageAccess, count: count + 1 }),
          250 * 4 ** count,
        );
    });
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [owner, api, hasStorageAccess, storageChecked, enabled, attempt]);
}

/** Detach hidden runtimes: reloading the list alone retains their decrypted messages. */
export async function reopenHistory({
  api,
  lostAccount,
  isCurrent,
}: {
  api: ReturnType<typeof useAui>;
  lostAccount: boolean;
  isCurrent: () => boolean;
}) {
  const draft = api.composer.getState().text;
  const store = useAssistantCloudStore.getState();
  // Signed out or switched account: no earlier conversation stays selected, so none can linger
  // under a stale id once the list reloads.
  if (lostAccount) {
    await api.threads.switchToNewThread();
    if (!isCurrent()) return false;
    if (draft) api.composer.setText(draft);
  }
  await store.reset();
  if (!isCurrent()) return false;
  try {
    // Without storage this loads an empty store, and the reload is what takes this page's
    // conversations off the list: detaching them only stops their runtimes.
    await store.initialize();
    if (!isCurrent()) return false;
    await api.threads.reload();
  } finally {
    if (isCurrent()) {
      await detachHiddenHistory({ api, alreadyNew: lostAccount, draft, isCurrent });
    }
  }
  return isCurrent();
}

async function detachHiddenHistory({
  api,
  alreadyNew,
  draft,
  isCurrent,
}: {
  api: ReturnType<typeof useAui>;
  alreadyNew: boolean;
  draft: string;
  isCurrent: () => boolean;
}) {
  const readable = useAssistantCloudStore.getState().threads;
  const hidden = Object.values(api.threads.getState().threadItems).filter(
    item => item.status !== 'new' && !readable.has(item.remoteId ?? item.id),
  );
  const state = api.threads.getState();
  if (
    !alreadyNew &&
    state.mainThreadId &&
    (!findThread(Object.values(state.threadItems), state.mainThreadId) ||
      hidden.some(item => item.id === state.mainThreadId))
  ) {
    await api.threads.switchToNewThread();
    if (!isCurrent()) return false;
    if (draft) api.composer.setText(draft);
  }
  for (const item of hidden) {
    await api.threads.item({ id: item.id }).detach();
    if (!isCurrent()) return false;
  }
  return true;
}
